/* eslint-disable @typescript-eslint/no-use-before-define */
import React, { useState } from 'react';
import { IExercisePlan, IPage, STEPS } from '../../../models/exercises/IExercisePlan';
import { CHAPTER_IDENTIFIER } from '../../../constants';
import { StyledAlertIcon, StyledAlertMessage, StyledCTAButton, StyledCTAButtonContainer, StyledDownloadButton, StyledEmptySpace, StyledNotificationContainer, StyledPage, StyledPageContentContainer, StyledPageDescription, StyledPageHeading, StyledStepperHeading } from './StrategyExercisePlan.styled';
import Content from 'services/Content';
import { StepOnePage } from './StepOne';
import { StepTwoBPage, StepTwoPage } from './StepTwo';
import { ProsAndConsList, StepThreePage } from './StepThree';
import { StepFourPage } from './StepFour';
import { StepFivePage } from './StepFive';
import { StepSixPage } from './StepSix';
import { FinalPage } from './FinalPage';
import { onDownloadButtonClick } from 'utils/helper';

interface IStrategyExercisePlanProps {
  onComplete: () => void
}

interface IPageContentsProps {
  content: IPage;
  children: React.ReactNode;
  onCtaClick?: () => void;
  isCtaButtonDisabled?: boolean;
  onDownloadButtonClick?: () => void;
}

export interface IStep {
  content: IPage;
  setStep: React.Dispatch<React.SetStateAction<STEPS>>;
  handleError?: (error: PlanActivityFormError) => void;
}

export interface IErrorProps {
  errorMessage?: string;
}

export type PlanActivityFormError = {
  isError: boolean,
  errorMessage: string
}

export type PreferredOption = {
  solution: string,
  prosAndCons: ProsAndConsList,
}

export const PageContents: React.FC<IPageContentsProps> = ({ content, children, onCtaClick, isCtaButtonDisabled = false }) => {
  const ref = React.useRef(null);
  const downloadButtonRef = React.useRef(null);
  const stepperHeading = content.step.split('.')[0] // .split below removes .B from STEP TWO.B
  const downloadButtonText = 'Download plan';
  return (
    <React.Fragment>
      <StyledPageContentContainer>
        <StyledEmptySpace />
        <StyledStepperHeading>{stepperHeading}</StyledStepperHeading> 
        <StyledPageHeading>{content.heading}</StyledPageHeading>
        {content.description && <StyledPageDescription>{content.description}</StyledPageDescription>}
        {children}
      </StyledPageContentContainer>
      <StyledCTAButtonContainer>
        {content.step === STEPS.FINAL && <StyledDownloadButton primary ref={downloadButtonRef} label={downloadButtonText} onClick={() =>onDownloadButtonClick(downloadButtonText, content.heading)} />}
        <StyledCTAButton ref={ref} label={content.buttonText} onClick={onCtaClick} isDisabled={isCtaButtonDisabled}/>
      </StyledCTAButtonContainer>
    </React.Fragment>
  )
}

export const ErrorNotification: React.FC<IErrorProps> = ({ errorMessage }) => {
  return (
    <StyledNotificationContainer>
      <StyledAlertIcon cmsSrc={'images/icons/plan/alert'} />
      <StyledAlertMessage>{errorMessage}</StyledAlertMessage>
    </StyledNotificationContainer>
  )
}

export const StrategyExercisePlan: React.FC<IStrategyExercisePlanProps> = ({ onComplete }) => {
  const content = Content.getChapterByIdentifier(CHAPTER_IDENTIFIER.ProblemSolving)?.exercise as IExercisePlan;
  const [currentStep, setStep] = useState<STEPS>(STEPS.STEP_ONE);
  const [error, setError] = useState<PlanActivityFormError>({ isError: false, errorMessage: '' });
  const [problemStatement, setProblem] = useState('');
  const [solutions, setSolutions] = useState<string[]>([]);
  const [selectedSolution, setSelectedSolution] = useState<string[]>([]);
  const [prosAndConsList, setProsAndConsList] = useState<ProsAndConsList[]>([]);
  const [preferredOption, setPreferredOption] = useState<PreferredOption | null>(null);
  const [planSteps, setPlanSteps] = useState<string[]>([]);

  const handleError = (errorState: PlanActivityFormError) => {
    setError({ isError: errorState.isError, errorMessage: errorState.errorMessage });
  }

  const preferredOptionHandler = (option: PreferredOption) => {
    setPreferredOption(option);
  }
  
  const getStepContent = (step: STEPS) => {
    const exercise = content.steps.find((page) => page.step === step);
    if (!exercise) {
      return null;
    }

    switch (step) {
      case STEPS.STEP_ONE:
        return <StepOnePage content={exercise} setStep={setStep} setProblem={setProblem} handleError={handleError}/>
      case STEPS.STEP_TWO:
        return <StepTwoPage content={exercise} setStep={setStep} setSolutions={setSolutions} handleError={handleError}/>
      case STEPS.STEP_TWO_B:
        return <StepTwoBPage content={exercise} setStep={setStep} solutions={solutions} handleError={handleError} setSelectedSolution={setSelectedSolution}/>
      case STEPS.STEP_THREE:
        return <StepThreePage content={exercise} setStep={setStep} selectedSolutions={selectedSolution} setPnCList={setProsAndConsList}/>
      case STEPS.STEP_FOUR:
        return <StepFourPage content={exercise} setStep={setStep} solutions={selectedSolution} prosAndConsList={prosAndConsList} preferredOptionHandler={preferredOptionHandler}/>
      case STEPS.STEP_FIVE:
        return <StepFivePage content={exercise} setStep={setStep} setPlanSteps={setPlanSteps} handleError={handleError}/>
      case STEPS.STEP_SIX:
        return <StepSixPage content={exercise} setStep={setStep}/>
      case STEPS.FINAL:
        return preferredOption && <FinalPage content={exercise} setStep={() => { }} solution={preferredOption?.solution} planSteps={planSteps} onComplete={onComplete}/>
    }
  }
  
  if (content) {
    return (
    <React.Fragment>
      {error.isError && <ErrorNotification errorMessage={error.errorMessage}/>}
      <StyledPage >
        {getStepContent(currentStep)}
      </StyledPage>
    </React.Fragment>
  )
  } else {
    return null;
  }
}